import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from '@edx/frontend-platform/i18n';
import {
  FormControl,
  FormControlFeedback,
  Icon,
  IconButton,
  Spinner,
} from '@openedx/paragon';
import { KeyboardArrowDown, KeyboardArrowUp } from '@openedx/paragon/icons';

const AuthFormAutosuggest = ({
  children,
  value,
  onChange,
  isLoading,
  screenReaderText,
  helpMessage,
  ...props
}) => {
  const intl = useIntl();
  const wrapperRef = React.useRef(null);
  const [isDropdownExpanded, setIsDropdownExpanded] = React.useState(false);
  const [displayValue, setDisplayValue] = React.useState(value?.userProvidedText || '');
  const [dropdownItems, setDropdownItems] = React.useState([]);
  const [activeMenuItemId, setActiveMenuItemId] = React.useState(null);

  const collapseDropdown = () => {
    setDropdownItems([]);
    setIsDropdownExpanded(false);
    setActiveMenuItemId(null);
  };

  const handleItemSelect = (event, originalOnClick) => {
    const selectedValue = event.currentTarget.getAttribute('data-value');
    const selectedId = event.currentTarget.id;
    setDisplayValue(selectedValue);
    if (onChange) {
      onChange({
        userProvidedText: selectedValue,
        selectionValue: selectedValue,
        selectionId: selectedId,
      });
    }
    collapseDropdown();
    if (originalOnClick) {
      originalOnClick(event);
    }
  };

  const getItems = (strToFind = '') => {
    let items = React.Children.map(children, (child) => {
      const { children: optionText, onClick, ...rest } = child.props;
      const optionId = child.props.id || `opt-${optionText}`;
      return React.cloneElement(child, {
        ...rest,
        id: optionId,
        'data-value': optionText,
        onClick: (event) => handleItemSelect(event, onClick),
        onFocus: () => setActiveMenuItemId(optionId),
      });
    }) || [];

    if (strToFind.length > 0) {
      items = items.filter(opt => opt.props.children.toLowerCase().includes(strToFind.toLowerCase()));
    }
    return items;
  };

  const expandDropdown = () => {
    setDropdownItems(getItems());
    setIsDropdownExpanded(true);
  };

  const handleTextInput = (event) => {
    const userProvidedText = event.target.value;
    if (!userProvidedText.length) {
      setDisplayValue('');
      collapseDropdown();
      if (onChange) {
        onChange({ userProvidedText: '', selectionValue: '', selectionId: '' });
      }
      return;
    }

    setDisplayValue(userProvidedText);
    const filteredItems = getItems(userProvidedText);
    setDropdownItems(filteredItems);
    setIsDropdownExpanded(true);

    const matchingDropdownItem = filteredItems.find(
      opt => opt.props.children.toLowerCase() === userProvidedText.toLowerCase(),
    );
    if (onChange) {
      if (matchingDropdownItem) {
        onChange({
          userProvidedText: matchingDropdownItem.props.children,
          selectionValue: matchingDropdownItem.props.children,
          selectionId: matchingDropdownItem.props.id,
        });
      } else {
        onChange({ userProvidedText, selectionValue: '', selectionId: '' });
      }
    }
  };

  React.useEffect(() => {
    setDisplayValue(value ? value.userProvidedText || '' : '');
  }, [value]);

  React.useEffect(() => {
    const handleDocumentClick = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        collapseDropdown();
      }
    };
    document.addEventListener('click', handleDocumentClick, true);
    return () => document.removeEventListener('click', handleDocumentClick, true);
  }, []);

  return (
    <div className="pgn__form-autosuggest__wrapper" ref={wrapperRef}>
      <div aria-live="assertive" className="sr-only" data-testid="autosuggest-screen-reader-options-count">
        {`${dropdownItems.length} options found`}
      </div>
      <FormControl
        aria-expanded={(dropdownItems.length > 0).toString()}
        aria-owns="pgn__form-autosuggest__dropdown-box"
        role="combobox"
        aria-autocomplete="list"
        autoComplete="off"
        value={displayValue}
        aria-activedescendant={activeMenuItemId}
        onChange={handleTextInput}
        onClick={expandDropdown}
        trailingElement={(
          <IconButton
            className="pgn__form-autosuggest__icon-button"
            data-testid="autosuggest-iconbutton"
            tabIndex="-1"
            src={isDropdownExpanded ? KeyboardArrowUp : KeyboardArrowDown}
            iconAs={Icon}
            size="sm"
            variant="secondary"
            alt={isDropdownExpanded
              ? intl.formatMessage({ id: 'pgn.FormAutosuggest.iconButtonClosed', defaultMessage: 'Close the options menu' })
              : intl.formatMessage({ id: 'pgn.FormAutosuggest.iconButtonOpened', defaultMessage: 'Open the options menu' })}
            onClick={() => (isDropdownExpanded ? collapseDropdown() : expandDropdown())}
          />
        )}
        data-testid="autosuggest-textbox-input"
        {...props}
      />
      {helpMessage && <FormControlFeedback type="default">{helpMessage}</FormControlFeedback>}
      <ul id="pgn__form-autosuggest__dropdown-box" className="pgn__form-autosuggest__dropdown" role="listbox">
        {isLoading ? (
          <div className="pgn__form-autosuggest__dropdown-loading">
            <Spinner animation="border" variant="dark" screenReaderText={screenReaderText} data-testid="autosuggest-loading-spinner" />
          </div>
        ) : dropdownItems.length > 0 && dropdownItems}
      </ul>
    </div>
  );
};

AuthFormAutosuggest.defaultProps = {
  onChange: null,
  value: null,
  isLoading: false,
  screenReaderText: 'loading',
  helpMessage: '',
  children: null,
};

AuthFormAutosuggest.propTypes = {
  children: PropTypes.node,
  value: PropTypes.shape({
    userProvidedText: PropTypes.string,
    selectionValue: PropTypes.string,
    selectionId: PropTypes.string,
  }),
  onChange: PropTypes.func,
  isLoading: PropTypes.bool,
  screenReaderText: PropTypes.string,
  helpMessage: PropTypes.string,
};

export default AuthFormAutosuggest;
